// Storage Notification Service
// Checks storage usage and shows notifications when organizations approach limits

import { storageUsageService, StorageUsage } from './storageUsageService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

export interface StorageNotification {
  organizationCode: string;
  organizationName: string;
  percentage: number;
  message: string;
  severity: 'warning' | 'critical';
}

class StorageNotificationService {
  private static instance: StorageNotificationService;
  private readonly WARNING_THRESHOLD = 80; // Show warning at 80%
  private readonly CRITICAL_THRESHOLD = 95; // Show critical at 95%

  private constructor() {}

  public static getInstance(): StorageNotificationService {
    if (!StorageNotificationService.instance) {
      StorageNotificationService.instance = new StorageNotificationService();
    }
    return StorageNotificationService.instance;
  }

  /**
   * Check all organizations for storage warnings
   */
  public async checkAllOrganizations(): Promise<StorageNotification[]> {
    try {
      const allUsage = await storageUsageService.getAllOrganizationsStorageUsage();
      const notifications: StorageNotification[] = [];

      for (const usage of allUsage) {
        if (usage.total.percentage >= this.WARNING_THRESHOLD) {
          const severity = usage.total.percentage >= this.CRITICAL_THRESHOLD ? 'critical' : 'warning';
          const message = this.generateMessage(usage, severity);
          
          notifications.push({
            organizationCode: usage.organizationCode,
            organizationName: usage.organizationName,
            percentage: usage.total.percentage,
            message,
            severity,
          });
        }
      }

      return notifications;
    } catch (error) {
      console.error('Error checking storage notifications:', error);
      return [];
    }
  }

  /**
   * Check a specific organization for storage warnings
   */
  public async checkOrganization(organizationCode: string): Promise<StorageNotification | null> {
    try {
      const usage = await storageUsageService.getStorageUsage(organizationCode);
      
      if (!usage) return null;

      if (usage.total.percentage >= this.WARNING_THRESHOLD) {
        const severity = usage.total.percentage >= this.CRITICAL_THRESHOLD ? 'critical' : 'warning';
        const message = this.generateMessage(usage, severity);
        
        return {
          organizationCode: usage.organizationCode,
          organizationName: usage.organizationName,
          percentage: usage.total.percentage,
          message,
          severity,
        };
      }

      return null;
    } catch (error) {
      console.error('Error checking organization storage:', error);
      return null;
    }
  }

  /**
   * Generate notification message
   */
  private generateMessage(usage: StorageUsage, severity: 'warning' | 'critical'): string {
    const remaining = storageUsageService.formatBytes(usage.total.limit - usage.total.used);
    const used = storageUsageService.formatBytes(usage.total.used);
    const limit = storageUsageService.formatBytes(usage.total.limit);

    if (severity === 'critical') {
      return `🚨 CRITICAL: ${usage.organizationName} has used ${usage.total.percentage.toFixed(1)}% of storage (${used} / ${limit}). Only ${remaining} remaining. Immediate action required!`;
    } else {
      return `⚠️ WARNING: ${usage.organizationName} has used ${usage.total.percentage.toFixed(1)}% of storage (${used} / ${limit}). ${remaining} remaining. Consider clearing storage soon.`;
    }
  }

  /**
   * Show toast notification
   */
  public showToast(notification: StorageNotification): void {
    const toast = document.createElement('div');
    toast.className = `storage-toast ${notification.severity}`;
    toast.innerHTML = `
      <div class="toast-content">
        <div class="toast-icon">${notification.severity === 'critical' ? '🚨' : '⚠️'}</div>
        <div class="toast-message">${notification.message}</div>
        <button class="toast-close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;
    
    // Add styles
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${notification.severity === 'critical' ? '#EA8F95' : '#FFA726'};
      color: white;
      padding: 16px 20px;
      border-radius: 12px;
      font-family: 'Nunito', sans-serif;
      font-weight: 600;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      max-width: 400px;
      animation: slideIn 0.3s ease;
    `;

    // Add animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      .storage-toast .toast-content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
      }
      .storage-toast .toast-icon {
        font-size: 24px;
        flex-shrink: 0;
      }
      .storage-toast .toast-message {
        flex: 1;
        font-size: 14px;
        line-height: 1.4;
      }
      .storage-toast .toast-close {
        background: none;
        border: none;
        color: white;
        font-size: 24px;
        cursor: pointer;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        opacity: 0.8;
      }
      .storage-toast .toast-close:hover {
        opacity: 1;
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(toast);
    
    // Auto-remove after 10 seconds for warnings, 15 seconds for critical
    const autoRemoveTime = notification.severity === 'critical' ? 15000 : 10000;
    setTimeout(() => {
      toast.style.animation = 'slideIn 0.3s ease reverse';
      setTimeout(() => {
        if (toast.parentElement) {
          toast.parentElement.removeChild(toast);
        }
      }, 300);
    }, autoRemoveTime);
  }

  /**
   * Check and show notifications for all organizations
   */
  public async checkAndNotify(): Promise<void> {
    const notifications = await this.checkAllOrganizations();
    
    // Show notifications with a small delay between them
    notifications.forEach((notification, index) => {
      setTimeout(() => {
        this.showToast(notification);
      }, index * 500); // 500ms delay between each notification
    });
  }
}

export const storageNotificationService = StorageNotificationService.getInstance();
export default StorageNotificationService;

